/**
 * Deterministic initials tile — the fallback for a host with no logo.
 *
 * Teams created by an admin carry no `icon` (nothing in the onboarding path
 * could set one until `iconUrl` landed on the MCP write tools), so every
 * host-facing surface fell back to `/images/avatar.png`: an identical grey
 * silhouette on the team profile, the chat header, the checkout parties block
 * and the dashboard. A real logo is always better — `yarn audit-team-branding`
 * in `spotme-server` lists who is still missing one — but the fallback should
 * look designed rather than broken.
 *
 * 🛑 NOT for anonymous surfaces. Initials leak identity: "GW" on a listing
 * that deliberately withholds its landlord is a much shorter guess than
 * nothing. Renter-facing listing pages and pre-booking chat use the neutral
 * crest / `avatarb.png` instead — see `Details/User.tsx` and `getChatProfile`.
 *
 * TYPE: Rubik either way (the site is Rubik-only since 2026-09-30). Every
 * consumer today is a dashboard / inbox / nav surface (the dense vocabulary).
 * Pass `variant="editorial"` for the `font-display` magazine treatment.
 */

/**
 * Five fills, every pair ≥ 4.5:1 (WCAG AA for the initials). Order is
 * load-bearing: `paletteFor` indexes into it, so a team keeps its slot. Slots
 * 0 and 4 were `blue-500` / `blue-600` under white (2.7:1 / 3.4:1).
 */
const PALETTE = [
  { bg: 'bg-blue-300', fg: 'text-blue-800' },
  { bg: 'bg-ink-700', fg: 'text-cream-300' },
  { bg: 'bg-blue-800', fg: 'text-cream-200' },
  { bg: 'bg-ink-100', fg: 'text-cream-300' },
  { bg: 'bg-ink-200', fg: 'text-white-500' }
] as const

const VARIANT_TYPE = {
  dense: 'font-sans font-semibold tracking-[0.02em]',
  editorial: 'font-display font-medium tracking-[0.04em]'
} as const

/**
 * Initials scale with the tile (container query) unless the caller sizes
 * them: ≈14px in a 40px tile, 9px floor in a 20px scope-pill avatar, where a
 * fixed `text-sm` used to spill past the circle.
 */
const DEFAULT_TEXT = 'text-[clamp(9px,36cqw,28px)]'

/**
 * Up to two initials. Legal-form words are dropped so every "… SA de CV"
 * doesn't collapse onto the same two letters, and so "Bodegas de la Sierra"
 * reads BS rather than BD.
 */
const LEGAL_FORMS = new Set([
  'sa',
  'sab',
  'sapi',
  'srl',
  'cv',
  'de',
  'del',
  'la',
  'las',
  'los',
  'y'
])

export const initialsFor = (name: string): string => {
  const words = name
    .trim()
    .split(/[\s.,]+/)
    .filter((w) => w.length > 0)
  const meaningful = words.filter((w) => !LEGAL_FORMS.has(w.toLowerCase()))
  const picked = meaningful.length > 0 ? meaningful : words
  if (picked.length === 0) return '·'
  // A single-word name has no second initial to take, and one letter in a
  // 64px circle reads as a rendering bug — use its first two characters.
  if (picked.length === 1) return picked[0].slice(0, 2).toUpperCase()
  return picked
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

/** Stable index so the same team always gets the same colour. */
const paletteFor = (name: string) => {
  let hash = 0
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 100000
  }
  return PALETTE[hash % PALETTE.length]
}

export const Monogram = ({
  name,
  className,
  textClassName,
  variant = 'dense',
  decorative = false
}: {
  name: string
  /** Sizing + shape. Must set a size — there is no intrinsic image to imply one. */
  className?: string
  /** Type scale for the initials; size it against `className`. */
  textClassName?: string
  /** `dense` (default) = dashboards/nav; `editorial` = the `font-display` magazine treatment. */
  variant?: keyof typeof VARIANT_TYPE
  /**
   * Hide from assistive tech when the name is printed right beside the tile,
   * so a screen reader doesn't announce it twice.
   */
  decorative?: boolean
}) => {
  const { bg, fg } = paletteFor(name)
  const a11y = decorative
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-label': name } as const)
  return (
    <div
      {...a11y}
      className={`@container flex shrink-0 items-center justify-center rounded-full select-none ${VARIANT_TYPE[variant]} ${bg} ${fg} ${
        className ?? 'size-10'
      }`}
    >
      <span className={`leading-none ${textClassName ?? DEFAULT_TEXT}`}>
        {initialsFor(name)}
      </span>
    </div>
  )
}
